import React, { useEffect } from 'react';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { Society } from '../../../../app/models/Society';
import { Client } from '../../../../app/models/Client';
import { TransportationQuoteDetail } from '../../../../app/models/TransportationQuoteDetail';
import { WarehouseQuoteDetail } from '../../../../app/models/WareHouseQuoteDetail';
import { OrderDataForm } from '../../../../app/models/Order';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import LazyLoading from '../../../../components/LazyLoading';
import { SectorBusinessRulesEnum } from '../../../../app/shared/enums';

interface Props {
    orderForm: OrderDataForm;
    setOrderForm: (order: OrderDataForm) => void;
    societies: Society[];
    clients: Client[];
    transportationQuotes: TransportationQuoteDetail[];
    warehouseQuotes: WarehouseQuoteDetail[];
    sectors?: any[];
    errorFields?: any;
    loadingClient?: boolean;
    isCreated?: boolean;
    societyAndClientDisabled?: boolean;
}

const OrderForm = ({
    orderForm,
    setOrderForm,
    societies,
    clients,
    transportationQuotes,
    warehouseQuotes,
    sectors = [],
    errorFields,
    loadingClient,
    isCreated = false,
    societyAndClientDisabled = false,
}: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors, errors } =
        useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setOrderForm({ ...orderForm, [name]: value });
    };

    const handleSelect = (name: string, value: number | string | null) => {
        if (name === 'society_id') {
            setErrors({ ...errors, client_id: '' });
        }
        setOrderForm({ ...orderForm, [name]: value });
    };

    const handleClientSelect = (name: string, value: number | string | null) => {
        setOrderForm({
            ...orderForm,
            [name]: value,
            contact_email: clients.find((c) => c.id == value)?.email || '',
        });
    };

    const handleSectorChange = (sectorId: number) => {
        const selectedSector = sectors.find((s) => s.id === sectorId);
        const isWarehouse = selectedSector?.sector_business_rules_enum === SectorBusinessRulesEnum.WHS_Almacenamiento;
        setOrderForm({
            ...orderForm,
            sector_id: sectorId,
            sector: isWarehouse ? 'ALMACENAMIENTO' : 'TRANSPORTE',
            transportation_quote_detail_id: undefined,
            warehouse_quote_detail_id: undefined,
        });
    };

    if (!orderForm) return null;

    const isTransporte = orderForm.sector === 'TRANSPORTE';

    // Solo se muestran las cotizaciones que pertenecen al sector (Product Sector)
    // seleccionado en el combo "Sector", para no mezclar posiciones de otros
    // sectores que comparten el mismo flujo de negocio (ej. Transporte vs Shipping).
    const filteredTransportationQuotes = orderForm.sector_id
        ? transportationQuotes.filter((q: any) => q.sector_id === orderForm.sector_id)
        : transportationQuotes;

    const filteredWarehouseQuotes = orderForm.sector_id
        ? warehouseQuotes.filter((q: any) => q.sector_id === orderForm.sector_id)
        : warehouseQuotes;

    return (
        <>
            {/* ── Sector ──────────────────────────────────────────── */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Sector</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="sector_id"
                        id="sector_id"
                        inputValue={orderForm.sector_id}
                        options={sectors.map((s) => ({ value: s.id, label: s.name }))}
                        hasError={fieldHasError('sector') !== ''}
                        onChange={(opt: any) => handleSectorChange(opt?.value)}
                        placeholder="Seleccione un sector"
                        onFocus={() => onFocusRemove('sector')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('sector')}
                    </div>
                </div>
            </div>

            {/* ── Cotización (dinámica según sector) ──────────────── */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Cotización</label>
                <div className="col-md-9">
                    {isTransporte ? (
                        <SelectTwo
                            name="transportation_quote_detail_id"
                            id="transportation_quote_detail_id"
                            inputValue={orderForm.transportation_quote_detail_id}
                            options={SelectTwoMapperOptions(filteredTransportationQuotes)}
                            hasError={fieldHasError('transportation_quote_detail_id') !== ''}
                            onChange={(opt: any) =>
                                handleSelect('transportation_quote_detail_id', opt?.value ?? undefined)
                            }
                            placeholder="Seleccione posición de cotización de transporte"
                            onFocus={() => onFocusRemove('transportation_quote_detail_id')}
                            isClearable
                        />
                    ) : (
                        <SelectTwo
                            name="warehouse_quote_detail_id"
                            id="warehouse_quote_detail_id"
                            inputValue={orderForm.warehouse_quote_detail_id}
                            options={SelectTwoMapperOptions(filteredWarehouseQuotes)}
                            hasError={fieldHasError('warehouse_quote_detail_id') !== ''}
                            onChange={(opt: any) =>
                                handleSelect('warehouse_quote_detail_id', opt?.value ?? undefined)
                            }
                            placeholder="Seleccione posición de cotización de almacenamiento"
                            onFocus={() => onFocusRemove('warehouse_quote_detail_id')}
                            isClearable
                        />
                    )}
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage(
                            isTransporte
                                ? 'transportation_quote_detail_id'
                                : 'warehouse_quote_detail_id'
                        )}
                    </div>
                </div>
            </div>

            {/* ── Sociedad ─────────────────────────────────────────── */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Sociedad</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="society_id"
                        id="society_id"
                        inputValue={orderForm.society_id}
                        options={SelectTwoMapperOptions(societies)}
                        hasError={fieldHasError('society_id') !== ''}
                        onChange={(opt: any) => handleSelect('society_id', opt?.value)}
                        placeholder="Seleccione una sociedad"
                        onFocus={() => onFocusRemove('society_id')}
                        disabled={societyAndClientDisabled}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_id')}
                    </div>
                </div>
            </div>

            {/* ── Cliente ──────────────────────────────────────────── */}
            {loadingClient ? (
                <LazyLoading height={60} />
            ) : (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Cliente</label>
                    <div className="col-md-9">
                        <SelectTwo
                            name="client_id"
                            id="client_id"
                            inputValue={orderForm.client_id}
                            options={SelectTwoMapperOptions(clients, 'id', ['id_number', 'name'])}
                            hasError={fieldHasError('client_id') !== ''}
                            onChange={(opt: any) =>
                                handleClientSelect('client_id', opt?.value)
                            }
                            placeholder="Seleccione un cliente"
                            onFocus={() => onFocusRemove('client_id')}
                            disabled={societyAndClientDisabled}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('client_id')}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Email de contacto ────────────────────────────────── */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Contacto</label>
                <div className="col-md-9">
                    <input
                        name="contact_email"
                        id="contact_email"
                        type="email"
                        className={`form-control ${fieldHasError('contact_email')}`}
                        value={orderForm.contact_email}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('contact_email')}
                        placeholder="Ingrese un correo de contacto"
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('contact_email')}
                    </div>
                </div>
            </div>

        </>
    );
};

export default OrderForm;
